import { Component } from '@theme/component';

/** Line item property that marks a cart line as the free gift. Underscore-prefixed, so it's hidden in cart and checkout. */
const GIFT_PROPERTY = '_free_gift';

/**
 * Free gift picker shown in the cart drawer (see `snippets/cart-progress-bar.liquid`).
 * Adds/removes the gift through `Shopify.actions.updateCart`, whose lines-update event makes
 * `cart-items-component` re-render the drawer, which in turn re-renders this picker.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [track] - The scrollable list of gift cards.
 *
 * @extends {Component<Refs>}
 */
class FreeGiftPickerComponent extends Component {
  /**
   * Adds the gift variant of the clicked card to the cart.
   * @param {Event} event
   */
  add = async (event) => {
    const button = /** @type {HTMLButtonElement} */ (event.target);
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    await this.#update(button, {
      merchandiseId: variantId,
      quantity: 1,
      attributes: [{ key: GIFT_PROPERTY, value: 'true' }],
    });
  };

  /**
   * Removes the gift line from the cart.
   * @param {Event} event
   */
  remove = async (event) => {
    const button = /** @type {HTMLButtonElement} */ (event.target);
    const lineKey = button.dataset.lineKey;
    if (!lineKey || button.disabled) return;

    await this.#update(button, { id: lineKey, quantity: 0 });
  };

  /** @param {Event} event */
  scrollPrev = (event) => this.#scroll(event, -1);

  /** @param {Event} event */
  scrollNext = (event) => this.#scroll(event, 1);

  /**
   * @param {Event} _event
   * @param {number} direction
   */
  #scroll(_event, direction) {
    const { track } = this.refs;
    if (!track) return;
    track.scrollBy({ left: direction * track.clientWidth * 0.8, behavior: 'smooth' });
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {import('./standard-actions').CartLineInput} line
   */
  async #update(button, line) {
    const actions = /** @type {any} */ (window).Shopify?.actions;
    if (!actions?.updateCart) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    this.toggleAttribute('data-busy', true);

    try {
      const result = await actions.updateCart({ lines: [line] }, { event: { context: 'cart' } });
      if (result?.userErrors?.length) {
        console.warn('[free-gift-picker]', result.userErrors);
        this.#showError(result.userErrors[0].message);
      }
    } catch (error) {
      console.error('[free-gift-picker]', error);
      this.#showError('Something went wrong. Please try again.');
    } finally {
      // The drawer usually re-renders this markup; reset in case it didn't (e.g. on error).
      if (button.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
      this.removeAttribute('data-busy');
    }
  }

  /** @param {string} message */
  #showError(message) {
    const error = this.querySelector('[data-gift-error]');
    if (!error) return;
    error.textContent = message;
    error.removeAttribute('hidden');
  }
}

if (!customElements.get('free-gift-picker-component')) {
  customElements.define('free-gift-picker-component', FreeGiftPickerComponent);
}
